import styled from 'styled-components';
import { Spinner } from '../../util/spinner';

// TODO(hewitt): should be button props + children
interface AnyProps {
  [key: string]: any;
}

interface Props extends AnyProps {
  notPrimary?: boolean;
  isDangerous?: boolean;
  selected?: boolean;
  busy?: boolean;
  onClick: () => void;
}

export const Button = ({ children, notPrimary, isDangerous, selected, busy, onClick, ...buttonProps }: Props) => {
  const content = (
    <ButtonContent>
      {busy && <ButtonSpinner />}
      {children}
    </ButtonContent>
  );
  if (notPrimary) {
    return (
      // Note: Delaying the click with setTimeout() fixes page transition animation, but breaks input focus on iOS
      <ButtonWrapperBase
        $selected={selected}
        $isDangerous={isDangerous}
        $busy={busy}
        onClick={onClick}
        {...buttonProps}
      >
        {content}
      </ButtonWrapperBase>
    );
  }
  return (
    <ButtonWrapper onClick={onClick} $selected={selected} $isDangerous={isDangerous} $busy={busy} {...buttonProps}>
      {content}
    </ButtonWrapper>
  );
};

const ButtonWrapperBase = styled.button<{ $selected?: boolean; $isDangerous?: boolean; $busy?: boolean }>`
  /* the button itself is effectively the drop shadow behind the child div */
  font-family: Jost-SemiBold, sans-serif;
  height: 2.5em;
  width: 90%;
  cursor: pointer;
  border: none;
  opacity: ${(props) => (props.$busy ? 0.5 : 1)};
  pointer-events: ${(props) => (props.$busy ? 'none' : 'auto')};
  background-color: ${(props) =>
    props.$selected ? 'var(--color-button-selected-border)' : 'var(--color-button-border)'};
  border-radius: 15px;

  /* the child div is lifted up and colored with a border */
  & > div {
    transform: translateY(-4px);
    color: ${(props) => (props.$isDangerous ? 'var(--color-background)' : 'var(--color-text-button-onboarding)')};
    background-color: ${(props) =>
      props.$isDangerous
        ? 'var(--color-dangerous-button)'
        : props.$selected
          ? 'var(--color-button-selected-background)'
          : 'var(--color-background)'};
    border: solid 2px
      ${(props) => (props.$selected ? 'var(--color-button-selected-border)' : 'var(--color-button-border)')};
    border-radius: 15px;
  }

  /* on click, the child div drops down */
  &:active > div {
    transform: translateY(0);
  }
  /* disabled button remains dropped down & gray */
  &:disabled > div {
    transform: translateY(0);
    color: var(--color-text-disabled); /* stays gray — not the gold --color-text-light */
    background-color: var(--color-background-button-disabled);
    border: none;
  }
`;

const ButtonWrapper = styled(ButtonWrapperBase)`
  // because subclass declarations override base class declarations, we must increase the specificity
  // to avoid trumping base class declarations for disabled & active

  &:first-of-type:not(:disabled) {
    background-color: var(--color-button-border-dark);
  }

  &:first-of-type:not(:disabled) > div {
    color: var(--color-text-button-onboarding-primary);
    background-color: ${(props) =>
      props.$isDangerous ? 'var(--color-dangerous-button)' : 'var(--color-background-button-onboarding)'};
    border: solid 2px var(--color-button-border-dark);
  }

  &:first-of-type:not(:disabled):not(:active) > div {
    transform: translateY(-4px);
  }
`;

const ButtonContent = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

// absolutely positioned so the spinner does not shift the centered button label
const ButtonSpinner = styled(Spinner)`
  position: absolute;
  right: 0.75em;
  top: calc(50% - 0.5em);
  width: 1em;
  height: 1em;
  border-width: 2px;
  border-color: transparent;
  border-top-color: currentColor;
`;

export const LeftAlignedButton = styled(Button)`
  height: max-content;
  & > div {
    color: var(--color-text);
    justify-content: start;
  }
`;
